// client/src/pages/wholesaler/ProductManagement.js
// ================================================================
// PRODUCT MANAGEMENT — Phase B: Multi-select + Subcategory support
// - Checkbox selection for bulk operations (with range/drag select)
// - Bulk assign subcategory floating bar
// - Subcategory filter + SubcategoryManager modal
// - Registry-based category labels
// ================================================================
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { wholesalerAPI, registryAPI } from '../../services/api';
import Navbar from '../../components/common/Navbar';
import Footer from '../../components/common/Footer';
import ProductLimitBanner from '../../components/wholesaler/ProductLimitBanner';
import SubcategoryManager from '../../components/wholesaler/SubcategoryManager';
import { getImageUrl } from '../../utils/imageHelper';
import useCategoryLabels from '../../hooks/useCategoryLabels';
import useRangeSelect from '../../hooks/useRangeSelect';
import DragSelectOverlay from '../../components/common/DragSelectOverlay';
import toast from 'react-hot-toast';
import FloatingActionBar from '../../components/common/FloatingActionBar';
import ConfirmModal from '../../components/common/ConfirmModal';

const ProductManagement = () => {
  const { t, i18n } = useTranslation(['wholesaler', 'products', 'common']);
  const { getCategoryLabel, getUnitShort } = useCategoryLabels();
  const navigate = useNavigate();

  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedSubcategory, setSelectedSubcategory] = useState('');
  const [stockFilter, setStockFilter] = useState('all');
  const [visibilityFilter, setVisibilityFilter] = useState('all');
  const [tierInfo, setTierInfo] = useState(null);
  const [updatingStock, setUpdatingStock] = useState({});
  const [updatingVisibility, setUpdatingVisibility] = useState({});

  // Categories from registry
  const [categories, setCategories] = useState([]);

  // Subcategories
  const [subcategories, setSubcategories] = useState([]);
  const [showSubcatManager, setShowSubcatManager] = useState(false);

  // Multi-select (via hook)
  const {
    selectedIds, setSelectedIds, handleItemClick, toggleSelect,
    selectAll, clearSelection, dragProps, isDragging, dragRect, dragSelectedIds
  } = useRangeSelect(filteredProducts);

  const [bulkSubcategory, setBulkSubcategory] = useState('');
  const [bulkProcessing, setBulkProcessing] = useState(false);

  // Confirm modal
  const [confirmModal, setConfirmModal] = useState(null);

  // ERP Sync
  const [erpActive, setErpActive] = useState(false);
  const [erpSyncing, setErpSyncing] = useState(false);
  const [erpSyncResult, setErpSyncResult] = useState(null);

  useEffect(() => { fetchProducts(); fetchSubcategories(); }, []);
  useEffect(() => { fetchCategories(); }, [i18n.language, products]);
  useEffect(() => { filterProducts(); }, [products, searchTerm, selectedCategory, selectedSubcategory, stockFilter, visibilityFilter]);

  const handleErpProductSync = async () => {
    setErpSyncing(true);
    setErpSyncResult(null);
    try {
      const res = await wholesalerAPI.syncErpProducts();
      setErpSyncResult(res.data);
    } catch (err) {
      setErpSyncResult({ error: err.response?.data?.message || err.message });
    } finally {
      setErpSyncing(false);
    }
  };

  const fetchProducts = async () => {
    try {
      const [productsRes, profileRes] = await Promise.all([
        wholesalerAPI.getProducts(),
        wholesalerAPI.getProfile()
      ]);
      const productsList = productsRes.data.products || productsRes.data || [];
      setProducts(Array.isArray(productsList) ? productsList : []);

      const wholesaler = profileRes.data.wholesaler;
      const limits = { starter: 1500, professional: 5000, enterprise: -1 };
      setTierInfo({
        currentCount: productsList.length,
        limit: limits[wholesaler.subscriptionTier] || 1500,
        tier: wholesaler.subscriptionTier || 'starter'
      });
      setErpActive(wholesaler.subscriptionTier === 'enterprise' && wholesaler.erpIntegration?.isActive);
    } catch (err) {
      setError(t('wholesaler:products.fetchError', 'Failed to fetch products.'));
    } finally { setLoading(false); }
  };

  const fetchCategories = async () => {
    try {
      const lang = i18n.language === 'el' ? 'el' : 'en';
      const res = await registryAPI.getCategories(lang);
      if (res.data?.data) {
        // Filter to only categories the wholesaler already has products in
        const usedCategoryKeys = new Set(products.map(p => p.category));
        const filtered = res.data.data.filter(cat => usedCategoryKeys.has(cat.key));
        setCategories(filtered);
      }
    } catch (err) { /* fallback to getCategoryLabel */ }
  };

  const fetchSubcategories = async () => {
    try {
      const res = await wholesalerAPI.getSubcategories();
      setSubcategories(res.data?.data || []);
    } catch (err) { /* no subcategories yet */ }
  };

  const filterProducts = () => {
    let filtered = products;
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      filtered = filtered.filter(p =>
        p.name?.toLowerCase().includes(q) || p.description?.toLowerCase().includes(q) || p.sku?.toLowerCase().includes(q)
      );
    }
    if (selectedCategory) filtered = filtered.filter(p => p.category === selectedCategory);
    if (selectedSubcategory) filtered = filtered.filter(p => p.subcategory === selectedSubcategory);
    if (stockFilter !== 'all') filtered = filtered.filter(p => (p.stockStatus || 'in_stock') === stockFilter);
    if (visibilityFilter === 'visible') filtered = filtered.filter(p => (p.status || 'active') === 'active');
    else if (visibilityFilter === 'hidden') filtered = filtered.filter(p => p.status === 'inactive');
    setFilteredProducts(filtered);
  };

  const handleStockStatusChange = async (productId, newStatus) => {
    setUpdatingStock(prev => ({ ...prev, [productId]: true }));
    try {
      await wholesalerAPI.updateStockStatus(productId, newStatus);
      setProducts(prev => prev.map(p => p._id === productId ? { ...p, stockStatus: newStatus, lastStockUpdate: new Date() } : p));
    } catch (err) {
      toast.error(t('wholesaler:products.stockUpdateFailed', 'Failed to update stock status.'));
    } finally { setUpdatingStock(prev => ({ ...prev, [productId]: false })); }
  };

  const getStockCounts = () => {
    const counts = { all: products.length, in_stock: 0, low_stock: 0, out_of_stock: 0 };
    products.forEach(p => { counts[p.stockStatus || 'in_stock']++; });
    return counts;
  };

  const handleDelete = (productId) => {
    setConfirmModal({
      title: t('wholesaler:products.confirmDeleteTitle', 'Delete Product'),
      message: t('wholesaler:products.confirmDelete', 'Delete this product?'),
      icon: 'danger',
      confirmLabel: t('common:delete', 'Delete'),
      onConfirm: async () => {
        setConfirmModal(null);
        try { await wholesalerAPI.deleteProduct(productId); fetchProducts(); } catch (err) { toast.error(t('products.deleteProductFailed')); }
      },
    });
  };

  const handleToggleAvailability = async (productId, currentStatus) => {
    try { await wholesalerAPI.updateProduct(productId, { availability: { inStock: !currentStatus } }); fetchProducts(); }
    catch (err) { toast.error(t('products.availabilityFailed')); }
  };

  const handleClearSelection = () => { clearSelection(); setBulkSubcategory(''); };

  const handleBulkAssignSubcategory = async () => {
    if (selectedIds.size === 0) return;
    try {
      setBulkProcessing(true);
      await wholesalerAPI.bulkAssignSubcategory([...selectedIds], bulkSubcategory || null);
      handleClearSelection();
      fetchProducts();
    } catch (err) {
      toast.error(err.response?.data?.message || t('products.subcategoryAssignFailed'));
    } finally { setBulkProcessing(false); }
  };

  const handleBulkDelete = () => {
    if (selectedIds.size === 0) return;
    setConfirmModal({
      title: t('wholesaler:products.confirmBulkDeleteTitle', 'Delete Products'),
      message: t('wholesaler:products.confirmBulkDelete', 'Are you sure you want to delete {{count}} products?', { count: selectedIds.size }),
      icon: 'danger',
      confirmLabel: t('common:delete', 'Delete'),
      onConfirm: async () => {
        setConfirmModal(null);
        try {
          setBulkProcessing(true);
          await wholesalerAPI.bulkDeleteProducts([...selectedIds]);
          handleClearSelection();
          fetchProducts();
        } catch (err) {
          toast.error(err.response?.data?.message || t('products.bulkDeleteFailed'));
        } finally { setBulkProcessing(false); }
      },
    });
  };

  const handleVisibilityToggle = async (productId, currentStatus) => {
    const nextVisible = currentStatus !== 'active';
    setUpdatingVisibility(prev => ({ ...prev, [productId]: true }));
    setProducts(prev => prev.map(p => p._id === productId ? { ...p, status: nextVisible ? 'active' : 'inactive' } : p));
    try {
      await wholesalerAPI.setProductVisibility(productId, nextVisible);
      toast.success(nextVisible
        ? t('wholesaler:products.visibility.toastShown', 'Product is now visible to businesses')
        : t('wholesaler:products.visibility.toastHidden', 'Product is now hidden from businesses')
      );
    } catch (err) {
      setProducts(prev => prev.map(p => p._id === productId ? { ...p, status: currentStatus } : p));
      toast.error(err.response?.data?.message || t('wholesaler:products.visibility.updateFailed', 'Failed to update visibility'));
    } finally {
      setUpdatingVisibility(prev => ({ ...prev, [productId]: false }));
    }
  };

  const handleBulkVisibility = (visible) => {
    if (selectedIds.size === 0) return;
    setConfirmModal({
      title: visible
        ? t('wholesaler:products.visibility.confirmShowTitle', 'Show Products')
        : t('wholesaler:products.visibility.confirmHideTitle', 'Hide Products'),
      message: visible
        ? t('wholesaler:products.visibility.confirmShowBody', 'Make {{count}} products visible to businesses?', { count: selectedIds.size })
        : t('wholesaler:products.visibility.confirmHideBody', 'Hide {{count}} products from businesses? Sales reps can still pick them in OnBehalf orders.', { count: selectedIds.size }),
      icon: visible ? 'info' : 'warning',
      confirmLabel: visible
        ? t('wholesaler:products.visibility.showSelected', 'Show')
        : t('wholesaler:products.visibility.hideSelected', 'Hide'),
      onConfirm: async () => {
        setConfirmModal(null);
        try {
          setBulkProcessing(true);
          await wholesalerAPI.bulkSetProductVisibility([...selectedIds], visible);
          handleClearSelection();
          fetchProducts();
          toast.success(visible
            ? t('wholesaler:products.visibility.toastBulkShown', 'Products restored')
            : t('wholesaler:products.visibility.toastBulkHidden', 'Products hidden')
          );
        } catch (err) {
          toast.error(err.response?.data?.message || t('wholesaler:products.visibility.updateFailed', 'Failed to update visibility'));
        } finally { setBulkProcessing(false); }
      },
    });
  };

  const selectionVisibilityCounts = () => {
    let visible = 0, hidden = 0;
    selectedIds.forEach(id => {
      const p = products.find(x => x._id === id);
      if (!p) return;
      if (p.status === 'inactive') hidden++; else visible++;
    });
    return { visible, hidden };
  };

  // Get subcategories for selected category filter
  const getFilteredSubcategories = () => {
    if (!selectedCategory) return subcategories;
    return subcategories.filter(s => s.parentCategory === selectedCategory);
  };

  const getSubcategoryLabel = (key) => {
    const sub = subcategories.find(s => s.key === key);
    if (!sub) return key || '—';
    return i18n.language === 'el' ? sub.label_el : sub.label_en;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
      </div>
    );
  }

  const stockCounts = getStockCounts();
  const allSelected = filteredProducts.length > 0 && selectedIds.size === filteredProducts.length;

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <div className="pt-16 lg:pt-0">
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Header */}
          <div className="flex flex-wrap justify-between items-center gap-4 mb-8">
            <div>
              <h1 className="text-4xl font-bold text-gray-900">{t('wholesaler:products.title', 'Product Management')}</h1>
              <p className="text-gray-600 mt-2">
                {t('wholesaler:products.total', 'Total Products')}: {tierInfo?.currentCount || 0} / {tierInfo?.limit === -1 ? t('wholesaler:products.unlimited', 'Unlimited') : tierInfo?.limit || 1500} {t('wholesaler:products.allowed', 'allowed')}
              </p>
            </div>
            <div className="flex flex-wrap gap-3">
              <button
                onClick={() => setShowSubcatManager(true)}
                className="bg-indigo-600 text-white px-4 py-3 rounded-lg hover:bg-indigo-700 transition-colors font-semibold shadow-md text-sm"
              >
                📂 {t('wholesaler:subcategories.manage', 'Subcategories')}
              </button>
              <Link to="/wholesaler/products/bulk-upload"
                className="bg-green-600 text-white px-4 py-3 rounded-lg hover:bg-green-700 transition-colors font-semibold shadow-md text-sm">
                📤 {t('wholesaler:products.bulkUpload', 'Bulk Upload')}
              </Link>
              {erpActive && (
                <button
                  onClick={handleErpProductSync}
                  disabled={erpSyncing}
                  className="bg-purple-600 text-white px-4 py-3 rounded-lg hover:bg-purple-700 transition-colors font-semibold shadow-md text-sm disabled:opacity-50"
                >
                  {erpSyncing ? t('wholesaler:erp.sync.syncing', 'Syncing...') : t('wholesaler:erp.sync.syncProducts', 'Sync SoftOne Products')}
                </button>
              )}
              <Link to="/wholesaler/products/new"
                className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors font-semibold shadow-md">
                {t('wholesaler:products.addNew', '+ Add New Product')}
              </Link>
            </div>
          </div>

          {error && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">{error}</div>}

          {erpSyncResult && (
            <div className={`mb-4 p-4 rounded-lg ${erpSyncResult.error ? 'bg-red-50 border border-red-200' : 'bg-green-50 border border-green-200'}`}>
              {erpSyncResult.error ? (
                <p className="text-red-700">{erpSyncResult.error}</p>
              ) : (
                <div className="text-green-700 text-sm">
                  <p className="font-semibold">{t('wholesaler:erp.sync.productSyncComplete', 'Product sync complete')}</p>
                  <p>{t('wholesaler:erp.sync.total', 'Total in ERP')}: {erpSyncResult.total} · {t('wholesaler:erp.sync.matched', 'Matched')}: {erpSyncResult.matched} · {t('wholesaler:erp.sync.updated', 'Updated')}: {erpSyncResult.updated}</p>
                  {(erpSyncResult.priceUpdated > 0 || erpSyncResult.stockUpdated > 0) && (
                    <p>{t('wholesaler:erp.sync.pricesUpdated', 'Prices updated')}: {erpSyncResult.priceUpdated || 0} · {t('wholesaler:erp.sync.stockUpdated', 'Stock updated')}: {erpSyncResult.stockUpdated || 0}</p>
                  )}
                  {erpSyncResult.usedFallback && <p className="text-yellow-600">{t('wholesaler:erp.sync.usedFallback', 'Used fallback method (slower)')}</p>}
                </div>
              )}
              <button onClick={() => setErpSyncResult(null)} className="mt-2 text-xs text-gray-500 hover:text-gray-700">{t('common:dismiss', 'Dismiss')}</button>
            </div>
          )}

          {/* Filters */}
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Search */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('wholesaler:products.search', 'Search')}</label>
                <input type="text" placeholder={t('wholesaler:products.searchPlaceholder', 'Search by name, SKU...')}
                  value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" />
              </div>
              {/* Category */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('wholesaler:products.filterCategory', 'Category')}</label>
                <select value={selectedCategory} onChange={(e) => { setSelectedCategory(e.target.value); setSelectedSubcategory(''); }}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500">
                  <option value="">{t('wholesaler:products.allCategories', 'All Categories')}</option>
                  {categories.map(cat => (
                    <option key={cat.key} value={cat.key}>{cat.icon} {cat.label}</option>
                  ))}
                </select>
              </div>
              {/* Subcategory */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('wholesaler:products.filterSubcategory', 'Subcategory')}</label>
                <select value={selectedSubcategory} onChange={(e) => setSelectedSubcategory(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500">
                  <option value="">{t('wholesaler:products.allSubcategories', 'All Subcategories')}</option>
                  <option value="__none__">{t('wholesaler:products.noSubcategory', '— No subcategory —')}</option>
                  {getFilteredSubcategories().map(sub => (
                    <option key={sub.key} value={sub.key}>{i18n.language === 'el' ? sub.label_el : sub.label_en}</option>
                  ))}
                </select>
              </div>
              {/* Visibility */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('wholesaler:products.visibility.label', 'Visibility')}</label>
                <select value={visibilityFilter} onChange={(e) => setVisibilityFilter(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500">
                  <option value="all">{t('wholesaler:products.visibility.filterAll', 'All (visible + hidden)')}</option>
                  <option value="visible">{t('wholesaler:products.visibility.filterVisible', 'Visible only')}</option>
                  <option value="hidden">{t('wholesaler:products.visibility.filterHidden', 'Hidden only')}</option>
                </select>
              </div>
            </div>

            {/* Stock Tabs */}
            <div className="mt-6">
              <div className="flex flex-wrap gap-2">
                {[
                  { key: 'all', label: t('wholesaler:products.all', 'All'), color: 'blue' },
                  { key: 'in_stock', label: `🟢 ${t('wholesaler:products.inStock', 'In Stock')}`, color: 'green' },
                  { key: 'low_stock', label: `🟡 ${t('wholesaler:products.lowStock', 'Low Stock')}`, color: 'yellow' },
                  { key: 'out_of_stock', label: `🔴 ${t('wholesaler:products.outOfStock', 'Out of Stock')}`, color: 'red' },
                ].map(({ key, label, color }) => (
                  <button key={key} onClick={() => setStockFilter(key)}
                    className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                      stockFilter === key ? `bg-${color}-600 text-white shadow-md` : `bg-${color}-50 text-${color}-700 hover:bg-${color}-100`
                    }`}>
                    {label} ({stockCounts[key]})
                  </button>
                ))}
              </div>
            </div>

            <p className="text-sm text-gray-600 mt-4">
              {t('wholesaler:products.showing', 'Showing')} {filteredProducts.length} {t('wholesaler:products.of', 'of')} {products.length} {t('wholesaler:products.productsLabel', 'products')}
            </p>
          </div>

          {/* Tier Banner */}
          {tierInfo && <ProductLimitBanner currentCount={tierInfo.currentCount} limit={tierInfo.limit} tier={tierInfo.tier} onUpgradeClick={() => navigate('/wholesaler/subscription')} />}

          {/* Products Table */}
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            {filteredProducts.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-gray-500 text-lg mb-4">
                  {searchTerm || selectedCategory || stockFilter !== 'all'
                    ? t('wholesaler:products.noFilterResults', 'No products match your filters.')
                    : t('wholesaler:products.noProducts', 'No products yet.')}
                </p>
                {!searchTerm && !selectedCategory && stockFilter === 'all' && (
                  <Link to="/wholesaler/products/new" className="text-blue-600 hover:text-blue-700 font-medium">
                    {t('wholesaler:products.addProductLink', 'Add Product →')}
                  </Link>
                )}
              </div>
            ) : (
              <div className="overflow-x-auto relative" {...dragProps} ref={dragProps.ref}>
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left">
                        <input type="checkbox" checked={allSelected} onChange={selectAll}
                          className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('wholesaler:products.product', 'Product')}</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('wholesaler:products.categoryCol', 'Category')}</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('wholesaler:products.price', 'Price')}</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('wholesaler:products.stockStatus', 'Stock')}</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('wholesaler:products.actions', 'Actions')}</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredProducts.map((product) => {
                      const stockStatus = product.stockStatus || 'in_stock';
                      const isUpdating = updatingStock[product._id];
                      const isSelected = selectedIds.has(product._id);
                      const isDragHighlighted = dragSelectedIds.has(product._id);
                      const isHidden = product.status === 'inactive';
                      const isUpdatingVis = updatingVisibility[product._id];

                      return (
                        <tr key={product._id} data-select-id={product._id}
                          className={`hover:bg-gray-50 transition-colors ${isSelected ? 'bg-blue-50' : ''} ${isDragHighlighted && !isSelected ? 'bg-blue-50' : ''} ${isHidden ? 'opacity-60' : ''}`}>
                          {/* Checkbox */}
                          <td className="px-4 py-4">
                            <input type="checkbox" checked={isSelected} readOnly
                              onClick={(e) => { e.stopPropagation(); handleItemClick(product._id, e); }}
                              className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer" />
                          </td>
                          {/* Product */}
                          <td className="px-4 py-4">
                            <div className="flex items-center gap-3">
                              {product.images?.length > 0 ? (
                                <img src={getImageUrl(product.images[0])} alt={product.name}
                                  className="h-16 w-16 rounded-lg object-cover flex-shrink-0"
                                  onError={(e) => { if (!e.target.dataset.errorHandled) { e.target.dataset.errorHandled='true'; e.target.src="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='64' height='64'%3E%3Crect fill='%23f3f4f6' width='64' height='64'/%3E%3C/svg%3E"; }}} />
                              ) : (
                                <div className="h-16 w-16 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
                                  <span className="text-gray-400 text-xs">{t('products:noImage', 'No img')}</span>
                                </div>
                              )}
                              <div className="min-w-0">
                                <p className="font-semibold text-gray-900 truncate">{product.name}</p>
                                {product.sku && <p className="text-xs text-gray-400">SKU: {product.sku}</p>}
                              </div>
                            </div>
                          </td>
                          {/* Category + Subcategory */}
                          <td className="px-4 py-4">
                            <span className="text-sm text-gray-900">{getCategoryLabel(product.category)}</span>
                            {product.subcategory && (
                              <span className="block text-xs text-indigo-600 mt-0.5">↳ {getSubcategoryLabel(product.subcategory)}</span>
                            )}
                          </td>
                          {/* Price */}
                          <td className="px-4 py-4 whitespace-nowrap">
                            <span className="font-bold text-gray-900">€{(product.pricing?.unitPrice || product.price || 0).toFixed(2)}</span>
                            <span className="text-xs text-gray-500 ml-1">/{getUnitShort(product.pricing?.unit) || product.pricing?.unit}</span>
                          </td>
                          {/* Stock */}
                          <td className="px-4 py-4 whitespace-nowrap">
                            <div className="flex flex-col gap-1.5">
                              <select value={stockStatus} onChange={(e) => handleStockStatusChange(product._id, e.target.value)} disabled={isUpdating}
                                className={`px-2 py-1.5 rounded-lg text-xs font-medium border-0 cursor-pointer focus:ring-2 transition-all
                                  ${isUpdating ? 'opacity-50' : ''}
                                  ${stockStatus === 'in_stock' ? 'bg-green-100 text-green-800' : ''}
                                  ${stockStatus === 'low_stock' ? 'bg-yellow-100 text-yellow-800' : ''}
                                  ${stockStatus === 'out_of_stock' ? 'bg-red-100 text-red-800' : ''}
                                `}>
                                <option value="in_stock">🟢 {t('wholesaler:products.inStock', 'In Stock')}</option>
                                <option value="low_stock">🟡 {t('wholesaler:products.lowStock', 'Low Stock')}</option>
                                <option value="out_of_stock">🔴 {t('wholesaler:products.outOfStock', 'Out of Stock')}</option>
                              </select>
                              <button
                                type="button"
                                onClick={() => handleVisibilityToggle(product._id, product.status || 'active')}
                                disabled={isUpdatingVis}
                                title={isHidden
                                  ? t('wholesaler:products.visibility.toggleToShow', 'Click to make visible to businesses')
                                  : t('wholesaler:products.visibility.toggleToHide', 'Click to hide from businesses')}
                                className={`px-2 py-1 rounded-lg text-xs font-medium transition-all w-fit
                                  ${isUpdatingVis ? 'opacity-50' : 'hover:opacity-80 cursor-pointer'}
                                  ${isHidden ? 'bg-gray-200 text-gray-700' : 'bg-green-100 text-green-700'}
                                `}>
                                {isHidden
                                  ? `👁️‍🗨️ ${t('wholesaler:products.visibility.hidden', 'Hidden')}`
                                  : `👁️ ${t('wholesaler:products.visibility.visible', 'Visible')}`}
                              </button>
                            </div>
                          </td>
                          {/* Actions */}
                          <td className="px-4 py-4 whitespace-nowrap">
                            <div className="flex gap-2">
                              <Link to={`/wholesaler/products/edit/${product._id}`}
                                className="text-blue-600 hover:text-blue-800 font-medium text-sm">
                                {t('common:edit', 'Edit')}
                              </Link>
                              <button onClick={() => handleDelete(product._id)}
                                className="text-red-600 hover:text-red-800 font-medium text-sm">
                                {t('common:delete', 'Delete')}
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                <DragSelectOverlay isDragging={isDragging} dragRect={dragRect} />
              </div>
            )}
          </div>
        </main>

        {/* ── Floating Bulk Action Bar ── */}
        <FloatingActionBar count={selectedIds.size} onClear={handleClearSelection} onSelectAll={selectAll}>
          {/* Subcategory dropdown */}
          <select value={bulkSubcategory} onChange={(e) => setBulkSubcategory(e.target.value)}
            className="px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 min-w-[200px]">
            <option value="">{t('wholesaler:products.clearSubcategory', '— Clear subcategory —')}</option>
            {subcategories.map(sub => (
              <option key={`${sub.parentCategory}-${sub.key}`} value={sub.key}>
                {getCategoryLabel(sub.parentCategory)} → {i18n.language === 'el' ? sub.label_el : sub.label_en}
              </option>
            ))}
          </select>

          <button onClick={handleBulkAssignSubcategory} disabled={bulkProcessing}
            className="px-4 py-2.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-sm font-semibold disabled:opacity-50 transition-colors">
            {bulkProcessing ? '...' : t('wholesaler:products.assignSubcategory', 'Assign')}
          </button>

          {(() => {
            const counts = selectionVisibilityCounts();
            return (
              <>
                {counts.visible > 0 && (
                  <button onClick={() => handleBulkVisibility(false)} disabled={bulkProcessing}
                    className="px-4 py-2.5 bg-gray-600 text-white rounded-lg hover:bg-gray-700 text-sm font-semibold disabled:opacity-50 transition-colors">
                    {bulkProcessing ? '...' : t('wholesaler:products.visibility.hideSelected', 'Hide')}
                  </button>
                )}
                {counts.hidden > 0 && (
                  <button onClick={() => handleBulkVisibility(true)} disabled={bulkProcessing}
                    className="px-4 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-semibold disabled:opacity-50 transition-colors">
                    {bulkProcessing ? '...' : t('wholesaler:products.visibility.showSelected', 'Show')}
                  </button>
                )}
              </>
            );
          })()}

          <button onClick={handleBulkDelete} disabled={bulkProcessing}
            className="px-4 py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm font-semibold disabled:opacity-50 transition-colors">
            {bulkProcessing ? '...' : t('wholesaler:products.bulkDelete', 'Delete')}
          </button>
        </FloatingActionBar>

        {/* Confirm Modal */}
        <ConfirmModal
          isOpen={!!confirmModal}
          title={confirmModal?.title}
          message={confirmModal?.message}
          icon={confirmModal?.icon || 'warning'}
          confirmLabel={confirmModal?.confirmLabel}
          confirmColor="red"
          onConfirm={confirmModal?.onConfirm}
          onCancel={() => setConfirmModal(null)}
        />

        {/* Subcategory Manager Modal */}
        <SubcategoryManager
          isOpen={showSubcatManager}
          onClose={() => setShowSubcatManager(false)}
          onSubcategoriesChanged={fetchSubcategories}
        />

        <Footer />
      </div>
    </div>
  );
};

export default ProductManagement;
